import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { ResourceId, SaveableStudyNote } from '®shared/annotations';
import { AppConfig } from '®shared/config';
import {
  CONTAINER_MODE,
  ContainerModes,
} from '®shared/container-structure/common';
import { getItemMenuButtonId } from '®shared/focus';
import { Permissions, UserPermissions } from '®shared/permissions';
import { UriService } from '®shared/url-utils';
import { LoadableSaveableBoolean } from '®shared/user';

type SaveableStudyNoteAliased = SaveableStudyNote;

/**
 * Component offering a menu of item operations
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rl-view-item-menu',
  templateUrl: './view-item-menu.component.html',
})
export class ViewItemMenuComponent implements OnInit, OnChanges {
  ContainerModes = ContainerModes;

  @Input() areAnnotationsResolved: boolean;
  @Input() isSubscribedToResource?: LoadableSaveableBoolean;
  @Input() itemId: string;
  @Input() resourceId: string;
  @Input() permissions: UserPermissions;
  @Input() studyNote?: SaveableStudyNoteAliased;

  @Output() studyNoteEdit: EventEmitter<any> = new EventEmitter();
  @Output() addToMyBookmarks: EventEmitter<ResourceId> = new EventEmitter();
  @Output() toggleMenu = new EventEmitter<boolean>();
  @Output() citationExport = new EventEmitter<boolean>();
  @Output() reportBrokenLink = new EventEmitter<any>();
  @Output() shareItem = new EventEmitter<any>();

  public canUpdateStudyNote: boolean;
  public hasStudyNote = false;
  public hasListEditPermission = false;
  public risExportUrl: string;
  public resourceEditUrl: string;

  constructor(
    private appConfig: AppConfig,
    private cd: ChangeDetectorRef,
    @Inject(CONTAINER_MODE) public containerMode: ContainerModes,
    private uriService: UriService,
  ) {}

  onHidden() {
    // Force change detection to prevent a menu remaining open when it should
    // be closed. Only one menu should be open at a time.
    this.cd.detectChanges();
  }

  ngOnInit() {
    this.resourceEditUrl = this.uriService.getResourceEditUrlForItem(
      this.resourceId,
      this.itemId,
    );

    this.risExportUrl = this.uriService.getItemExportUrl(this.itemId);
  }

  ngOnChanges(_changes?: SimpleChanges) {
    this.hasStudyNote = this.studyNote ? true : false;
    const hasUpdateableStudyNote =
      this.hasStudyNote &&
      this.studyNote.isDeleting !== true &&
      this.studyNote.isSaving !== true;

    this.canUpdateStudyNote =
      this.areAnnotationsResolved &&
      (!this.hasStudyNote || hasUpdateableStudyNote);

    if (this.permissions) {
      this.hasListEditPermission = this.permissions.hasGlobalPermission(
        Permissions.EDIT_LIST,
      );
    }
  }

  get itemMenuButtonId(): string {
    return getItemMenuButtonId(this.itemId);
  }

  get isReportBrokenLinkEnabled(): boolean {
    return this.appConfig.tenant.featureFlags.isReportBrokenLinkEnabled;
  }

  onStudyNoteEdit(event) {
    this.studyNoteEdit.emit(event);
  }

  onExportItemCitation() {
    this.citationExport.emit(true);
  }

  onShowShareSheet() {
    this.shareItem.emit();
  }

  onShowReportBrokenLinkModal() {
    this.reportBrokenLink.emit();
  }
}
